/**
 * 开发期跨域代理包装（本地网站专用）。
 *
 * 背景：不少第三方 / 自建 OpenAI 兼容中转**不返回 CORS 头**
 * （`Access-Control-Allow-Origin` 缺失、`OPTIONS` 预检直接 403），
 * 浏览器会因此抛 `TypeError: Failed to fetch`，而同样的请求在服务端却能 200。
 * 本项目第一阶段交付的是**本地网页**，因此开发期把跨域请求改走
 * Vite dev server 的同源代理 `/__zxproxy`（见 `vite.config.ts` 的 `zxDevProxy`），
 * 由本地 Node 进程转发，绕开浏览器同源策略。
 *
 * 边界：
 * - **只在开发期生效**（`import.meta.env.DEV` 且非测试环境），生产/预览构建不受影响；
 * - **只作用于默认宿主 fetch**。调用方显式注入的 `fetch`（测试替身、自定义 transport）
 *   一律原样使用，绝不改写 —— 这是为了让测试与真实行为保持可预测。
 */

import type { FetchLike } from './types';

/** 代理端点（与 `vite.config.ts` 中注册的中间件路径一致）。 */
export const DEV_PROXY_PATH = '/__zxproxy';

/** 承载真实目标 URL 的请求头，由本地代理读取。 */
export const DEV_PROXY_TARGET_HEADER = 'x-zx-target';

/** 当前是否处于「需要走代理」的开发期（测试环境视为不需要，保证单测可预测）。 */
export function isDevProxyEnabled(): boolean {
  const env = (import.meta as { env?: { DEV?: boolean; MODE?: string } }).env;
  if (env?.DEV !== true) return false;
  // vitest 下 MODE === 'test'：此时必须保持原始 fetch 语义，避免测试被代理改写。
  return env.MODE !== 'test';
}

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function isCrossOrigin(target: string): boolean {
  if (typeof location === 'undefined') return false;
  try {
    return new URL(target).origin !== location.origin;
  } catch {
    return false;
  }
}

/**
 * 把宿主 fetch 包装成「跨域自动走本地代理」的版本。
 *
 * - 非开发期 / 同源请求 / 非 http(s) 请求：行为与传入的 `fetchImpl` 完全一致；
 * - 开发期跨域请求：改打 `/__zxproxy`，真实 URL 放进 `x-zx-target` 头，
 *   其余 method / headers / body / signal 原样透传。
 */
export function wrapWithDevProxy(fetchImpl: FetchLike): FetchLike {
  if (!isDevProxyEnabled()) return fetchImpl;
  return (input, init) => {
    const target = typeof input === 'string' ? input : input.toString();
    if (!isHttpUrl(target) || !isCrossOrigin(target)) {
      return fetchImpl(input, init);
    }
    const headers = new Headers(init?.headers ?? undefined);
    headers.set(DEV_PROXY_TARGET_HEADER, target);
    return fetchImpl(DEV_PROXY_PATH, { ...init, headers });
  };
}
